import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const MODULO = "AgendasdeViaje";

const normalizarTexto = (texto = "") =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

const ListarAgendasViatico = ({ refresh }) => {
  const [agendas, setAgendas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [ordenesViaje, setOrdenesViaje] = useState({});

  // Información flotante de la orden de viaje
  const [detalleOrden, setDetalleOrden] = useState(null);
  const [detalleOrdenFijado, setDetalleOrdenFijado] = useState(false);
  const [posicionDetalleOrden, setPosicionDetalleOrden] = useState({
    top: 0,
    left: 0,
  });

  const navigate = useNavigate();
  const { id } = useParams();

  // ==============================
  // CONFIG TOKEN
  // ==============================
  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  // ==============================
  // PERMISOS MÓDULO AGENDAS DE VIAJE
  // ==============================
  const getModuloAgendas = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
          ? JSON.parse(usuarioStorage)
          : userStorage
            ? JSON.parse(userStorage)
            : usuariosStorage
              ? JSON.parse(usuariosStorage)
              : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
        permisosData?.permisos ||
        permisosData?.usuario?.modulos ||
        permisosData?.usuario?.permisos ||
        permisosData?.user?.modulos ||
        permisosData?.user?.permisos ||
        [];

      const moduloBuscado = normalizarTexto(MODULO);

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo ||
            modulo?.modulo ||
            modulo?.nombre ||
            "";

          return normalizarTexto(nombre) === moduloBuscado;
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const moduloAgendas = useMemo(() => getModuloAgendas(), []);

  const puedeCrear =
    moduloAgendas?.puedeCrear === true ||
    moduloAgendas?.crear === true ||
    moduloAgendas?.create === true;

  const puedeActualizar =
    moduloAgendas?.puedeActualizar === true ||
    moduloAgendas?.actualizar === true ||
    moduloAgendas?.editar === true ||
    moduloAgendas?.update === true;

  const puedeEliminar =
    moduloAgendas?.puedeEliminar === true ||
    moduloAgendas?.eliminar === true ||
    moduloAgendas?.delete === true;

  // ==============================
  // VALIDAR SESIÓN
  // ==============================
  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");
          navigate("/login");
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  // ==============================
  // FORMATEAR FECHA
  // ==============================
  const formatearFechaSinDesfase = useCallback((fecha) => {
    if (!fecha) return "-";

    try {
      if (typeof fecha === "string" && /^\d{4}-\d{2}-\d{2}$/.test(fecha)) {
        const [year, month, day] = fecha.split("-");
        return `${day}/${month}/${year}`;
      }

      if (typeof fecha === "string" && fecha.includes("T")) {
        const parteFecha = fecha.split("T")[0];

        if (/^\d{4}-\d{2}-\d{2}$/.test(parteFecha)) {
          const [year, month, day] = parteFecha.split("-");
          return `${day}/${month}/${year}`;
        }
      }

      const d = new Date(fecha);

      if (Number.isNaN(d.getTime())) return "-";

      const day = String(d.getUTCDate()).padStart(2, "0");
      const month = String(d.getUTCMonth() + 1).padStart(2, "0");
      const year = d.getUTCFullYear();

      return `${day}/${month}/${year}`;
    } catch {
      return "-";
    }
  }, []);

  // ==============================
  // MESES PARA NOMBRE DEL ARCHIVO
  // ==============================
  const MESES_ES = useMemo(
    () => [
      "ENERO",
      "FEBRERO",
      "MARZO",
      "ABRIL",
      "MAYO",
      "JUNIO",
      "JULIO",
      "AGOSTO",
      "SEPTIEMBRE",
      "OCTUBRE",
      "NOVIEMBRE",
      "DICIEMBRE",
    ],
    []
  );

  // ==============================
  // LIMPIAR PARTES DEL NOMBRE
  // ==============================
  const limpiarParteNombreArchivo = useCallback((valor, fallback = "SIN_DATO") => {
    const texto = String(valor ?? "").trim();

    if (!texto) return fallback;

    return texto
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^\w-]/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_+|_+$/g, "") || fallback;
  }, []);

  // ==============================
  // OBTENER DÍA SIN DESFASE
  // ==============================
  const obtenerDiaFecha = useCallback((fecha) => {
    if (!fecha) return null;

    try {
      if (typeof fecha === "string") {
        const parteFecha = fecha.includes("T")
          ? fecha.split("T")[0]
          : fecha;

        if (/^\d{4}-\d{2}-\d{2}$/.test(parteFecha)) {
          const dia = Number(parteFecha.split("-")[2]);
          return Number.isFinite(dia) ? dia : null;
        }
      }

      const d = new Date(fecha);

      if (Number.isNaN(d.getTime())) return null;

      return d.getUTCDate();
    } catch {
      return null;
    }
  }, []);

  // ==============================
  // OBTENER MES SIN DESFASE
  // ==============================
  const obtenerMesFecha = useCallback((fecha) => {
    if (!fecha) return null;

    try {
      if (typeof fecha === "string") {
        const parteFecha = fecha.includes("T")
          ? fecha.split("T")[0]
          : fecha;

        if (/^\d{4}-\d{2}-\d{2}$/.test(parteFecha)) {
          const mes = Number(parteFecha.split("-")[1]);
          return mes >= 1 && mes <= 12 ? mes : null;
        }
      }

      const d = new Date(fecha);

      if (Number.isNaN(d.getTime())) return null;

      return d.getUTCMonth() + 1;
    } catch {
      return null;
    }
  }, []);

  // ==============================
  // GENERAR NOMBRE DE AGENDA
  // MISMO FORMATO DEL BACKEND
  //
  // AGENDA_DOCUMENTO_DIAS_MES.xlsx
  // ==============================
  const generarNombreArchivoAgenda = useCallback(
    (agenda, documentoInstructorConfirmado = "") => {
      const documentoInstructor =
        documentoInstructorConfirmado ||
        agenda?.documentoInstructor ||
        agenda?.contrato?.instructor?.documentoInstructor ||
        agenda?.contrato?.documentoInstructor ||
        "SIN_DOC";

      const agendaDias = Array.isArray(agenda?.agendaDias)
        ? agenda.agendaDias
        : [];

      const diasAgenda = agendaDias
        .map((dia) => obtenerDiaFecha(dia?.fecha))
        .filter((dia) => Number.isFinite(dia))
        .sort((a, b) => a - b)
        .join("_");

      const fechaPrimerDia =
        agendaDias?.[0]?.fecha ||
        agenda?.fechaInicioDesplazamiento ||
        agenda?.fechaInicio ||
        null;

      const numeroMes = obtenerMesFecha(fechaPrimerDia);

      const mesAgenda =
        numeroMes >= 1 && numeroMes <= 12
          ? MESES_ES[numeroMes - 1]
          : "SIN_MES";

      const documentoSeguro = limpiarParteNombreArchivo(
        documentoInstructor,
        "SIN_DOC"
      );

      return `AGENDA_${documentoSeguro}_${diasAgenda || "SIN_DIAS"}_${mesAgenda}.xlsx`;
    },
    [
      MESES_ES,
      limpiarParteNombreArchivo,
      obtenerDiaFecha,
      obtenerMesFecha,
    ]
  );

  // ==============================
  // DESCARGAR EXCEL
  // ==============================
  const descargarAgendaExcel = useCallback(
    async (agenda) => {
      let agendaId = null;

      try {
        if (!agenda || typeof agenda !== "object" || !agenda._id) {
          Swal.fire(
            "Error",
            "No se encontró la información de la agenda para descargar.",
            "error"
          );

          return;
        }

        agendaId = agenda._id;

        setDownloadingId(agendaId);

        const response = await axios.get(
          `/api/agendasviaticos/generar-excel/${agendaId}`,
          {
            ...authConfig(),
            responseType: "blob",
          }
        );

        // ==============================
        // OBTENER DOCUMENTO DEL INSTRUCTOR
        // ==============================
        //
        // Esta pantalla se abre por contrato, por eso `id`
        // corresponde al contrato usado en:
        // /api/agendasviaticos/listbycontrato/${id}
        //
        // Consultamos el contrato directamente para garantizar
        // que el nombre incluya documentoInstructor.
        // ==============================
        let documentoInstructor =
          agenda?.documentoInstructor ||
          agenda?.contrato?.instructor?.documentoInstructor ||
          agenda?.contrato?.documentoInstructor ||
          "";

        if (!documentoInstructor && id) {
          const responseContrato = await axios.get(
            `/api/contratos/listid/${id}`,
            authConfig()
          );

          const dataContrato = responseContrato?.data || {};

          const contratoCargado =
            dataContrato?.contrato ||
            dataContrato?.message ||
            null;

          documentoInstructor =
            contratoCargado?.instructor?.documentoInstructor ||
            contratoCargado?.documentoInstructor ||
            "";
        }

        if (!documentoInstructor) {
          throw new Error(
            "No fue posible obtener el documento de identidad del instructor."
          );
        }

        // ==============================
        // NOMBRE GENERADO POR FRONTEND
        // NO USA EL ID COMO NOMBRE
        // ==============================
        const fileName = generarNombreArchivoAgenda(
          agenda,
          documentoInstructor
        );

        console.log("[AGENDA] ID:", agendaId);
        console.log(
          "[AGENDA] Documento instructor:",
          documentoInstructor
        );
        console.log("[AGENDA] Nombre generado:", fileName);

        // ==============================
        // CREAR ARCHIVO EXCEL
        // ==============================
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        if (!blob.size) {
          throw new Error("El archivo recibido está vacío.");
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.setAttribute("download", fileName);

        document.body.appendChild(link);
        link.click();
        link.remove();

        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000);
      } catch (error) {
        console.error("Error al descargar la agenda:", error);

        if (manejarErrorSesion(error)) return;

        Swal.fire(
          "Error",
          error.response?.data?.message ||
            error.message ||
            "No se pudo descargar la agenda en Excel.",
          "error"
        );
      } finally {
        setDownloadingId(null);
      }
    },
    [generarNombreArchivoAgenda, manejarErrorSesion, id]
  );

  // ==============================
  // ELIMINAR AGENDA
  // ==============================
  const deleteAgenda = useCallback(
    async (agendaId) => {
      if (!puedeEliminar) {
        Swal.fire(
          "Sin permisos",
          "No tiene permisos para eliminar agendas de viaje.",
          "warning"
        );

        return false;
      }

      try {
        const result = await Swal.fire({
          title: "¿Está seguro?",
          text: "¡No podrá revertir este proceso!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Sí, eliminar",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
        });

        if (!result.isConfirmed) return false;

        setDeletingId(agendaId);

        const { data } = await axios.delete(
          `/api/agendasviaticos/delete/${agendaId}`,
          authConfig()
        );

        setAgendas((prev) =>
          prev.filter((agenda) => agenda._id !== agendaId)
        );

        Swal.fire({
          icon: "success",
          title: data?.message || "Agenda eliminada correctamente",
          showConfirmButton: false,
          timer: 1500,
        });

        return true;
      } catch (error) {
        console.error("Error al eliminar la agenda:", error);

        if (manejarErrorSesion(error)) return false;

        Swal.fire(
          "Error",
          error.response?.data?.message || "No se pudo eliminar la agenda.",
          "error"
        );

        return false;
      } finally {
        setDeletingId(null);
      }
    },
    [puedeEliminar, manejarErrorSesion]
  );

  // ==============================
  // CARGAR ÓRDENES DE VIAJE
  // Y RESUMEN DE LA LEGALIZACIÓN
  // ==============================
  const cargarOrdenesViaje = useCallback(
    async (listaAgendas = []) => {
      if (!Array.isArray(listaAgendas) || listaAgendas.length === 0) {
        setOrdenesViaje({});
        return;
      }

      const resultados = await Promise.all(
        listaAgendas.map(async (agenda) => {
          if (!agenda?._id) {
            return [null, null];
          }

          const totalDias = Array.isArray(agenda?.agendaDias)
            ? agenda.agendaDias.length
            : 0;

          try {
            const { data } = await axios.get(
              `/api/legalizacionesviaticos/agenda/${agenda._id}`,
              authConfig()
            );

            const legalizacion = data?.legalizacion || null;

            if (!legalizacion) {
              return [
                agenda._id,
                {
                  tieneLegalizacion: false,
                  numeroOrdenViaje: "",
                  totalDias,
                  diasConFotografico: 0,
                  diasConAsistencia: 0,
                  totalFotografias: 0,
                  totalAsistencias: 0,
                  estadoLegalizacion: "",
                  ciudadPresentacion: "",
                  fechaPresentacion: null,
                  observacionesRevision: "",
                },
              ];
            }

            const soportesPorDia = Array.isArray(legalizacion?.soportesPorDia)
              ? legalizacion.soportesPorDia
              : [];

            const diasConFotografico = soportesPorDia.filter(
              (soporte) =>
                Array.isArray(soporte?.registroFotografico) &&
                soporte.registroFotografico.length > 0
            ).length;

            const diasConAsistencia = soportesPorDia.filter(
              (soporte) =>
                Array.isArray(soporte?.registroAsistencia) &&
                soporte.registroAsistencia.length > 0
            ).length;

            const totalFotografias = soportesPorDia.reduce(
              (total, soporte) =>
                total +
                (Array.isArray(soporte?.registroFotografico)
                  ? soporte.registroFotografico.length
                  : 0),
              0
            );

            const totalAsistencias = soportesPorDia.reduce(
              (total, soporte) =>
                total +
                (Array.isArray(soporte?.registroAsistencia)
                  ? soporte.registroAsistencia.length
                  : 0),
              0
            );

            return [
              agenda._id,
              {
                tieneLegalizacion: true,
                numeroOrdenViaje: String(
                  legalizacion?.numeroOrdenViaje || ""
                ).trim(),
                totalDias,
                diasConFotografico,
                diasConAsistencia,
                totalFotografias,
                totalAsistencias,
                estadoLegalizacion: String(
                  legalizacion?.estadoLegalizacion || "borrador"
                )
                  .trim()
                  .toLowerCase(),
                ciudadPresentacion:
                  legalizacion?.ciudadPresentacion || "",
                fechaPresentacion:
                  legalizacion?.fechaPresentacion || null,
                observacionesRevision:
                  legalizacion?.observacionesRevision || "",
              },
            ];
          } catch (error) {
            // 404 = la agenda todavía no tiene legalización.
            if (error?.response?.status === 404) {
              return [
                agenda._id,
                {
                  tieneLegalizacion: false,
                  numeroOrdenViaje: "",
                  totalDias,
                  diasConFotografico: 0,
                  diasConAsistencia: 0,
                  totalFotografias: 0,
                  totalAsistencias: 0,
                  estadoLegalizacion: "",
                  ciudadPresentacion: "",
                  fechaPresentacion: null,
                  observacionesRevision: "",
                },
              ];
            }

            if (error?.response?.status === 401) {
              throw error;
            }

            console.warn(
              `No se pudo obtener la legalización de la agenda ${agenda._id}:`,
              error
            );

            return [
              agenda._id,
              {
                tieneLegalizacion: false,
                numeroOrdenViaje: "",
                totalDias,
                diasConFotografico: 0,
                diasConAsistencia: 0,
                totalFotografias: 0,
                totalAsistencias: 0,
                estadoLegalizacion: "",
                ciudadPresentacion: "",
                fechaPresentacion: null,
                observacionesRevision: "",
              },
            ];
          }
        })
      );

      const mapaOrdenes = resultados.reduce((acc, [agendaId, informacion]) => {
        if (agendaId) {
          acc[agendaId] = informacion;
        }

        return acc;
      }, {});

      setOrdenesViaje(mapaOrdenes);
    },
    []
  );

  // ==============================
  // CARGAR DATOS
  // ==============================
  const getData = useCallback(async () => {
    try {
      if (!id) return;

      setLoading(true);

      const { data } = await axios.get(
        `/api/agendasviaticos/listbycontrato/${id}`,
        authConfig()
      );

      const lista = Array.isArray(data?.agendas) ? data.agendas : [];

      // ORDEN DESCENDENTE:
      // El último registro agregado queda primero en la tabla.
      const obtenerFechaOrden = (agenda) => {
        if (agenda?.createdAt) {
          const fecha = new Date(agenda.createdAt).getTime();
          if (!Number.isNaN(fecha)) return fecha;
        }

        if (agenda?.fechaCreacion) {
          const fecha = new Date(agenda.fechaCreacion).getTime();
          if (!Number.isNaN(fecha)) return fecha;
        }

        if (agenda?._id && agenda._id.length >= 8) {
          return parseInt(agenda._id.substring(0, 8), 16) * 1000;
        }

        return 0;
      };

      const agendasOrdenadas = [...lista].sort(
        (a, b) => obtenerFechaOrden(b) - obtenerFechaOrden(a)
      );

      setAgendas(agendasOrdenadas);

      await cargarOrdenesViaje(agendasOrdenadas);
    } catch (error) {
      console.error("Error al cargar las agendas:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire(
        "Error",
        error.response?.data?.message ||
        "No se pudieron cargar las agendas del contrato.",
        "error"
      );

      setAgendas([]);
      setOrdenesViaje({});
    } finally {
      setLoading(false);
    }
  }, [id, manejarErrorSesion, cargarOrdenesViaje]);


  // ==============================
  // DUPLICAR AGENDA
  // ==============================
  const duplicarAgenda = useCallback(
    async (agendaId) => {
      if (!puedeCrear) {
        Swal.fire(
          "Sin permisos",
          "No tiene permisos para duplicar agendas de viaje.",
          "warning"
        );

        return;
      }

      try {
        const result = await Swal.fire({
          title: "¿Duplicar agenda?",
          text: "Se creará una nueva agenda con la misma información.",
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "Sí, duplicar",
          cancelButtonText: "Cancelar",
        });

        if (!result.isConfirmed) return;

        setDuplicatingId(agendaId);

        const { data } = await axios.post(
          `/api/agendasviaticos/duplicate/${agendaId}`,
          {},
          authConfig()
        );

        Swal.fire({
          icon: "success",
          title: data?.message || "Agenda duplicada correctamente",
          showConfirmButton: false,
          timer: 1500,
        });

        getData();
      } catch (error) {
        console.error("Error al duplicar agenda:", error);

        if (manejarErrorSesion(error)) return;

        Swal.fire(
          "Error",
          error.response?.data?.message || "No se pudo duplicar la agenda.",
          "error"
        );
      } finally {
        setDuplicatingId(null);
      }
    },
    [puedeCrear, manejarErrorSesion, getData]
  );

  useEffect(() => {
    getData();
  }, [getData, refresh]);

  // ==============================
  // ESTADO VISUAL DE LA ORDEN
  // ==============================
  const obtenerEstadoVisualOrden = useCallback((info) => {
    if (!info?.tieneLegalizacion || !info?.numeroOrdenViaje) {
      return {
        key: "sin_orden",
        texto: "Sin orden",
        color: "#6c757d",
        fondo: "#f8f9fa",
        borde: "#dee2e6",
      };
    }

    const totalDias = Number(info?.totalDias || 0);

    const fotograficoCompleto =
      totalDias > 0 &&
      Number(info?.diasConFotografico || 0) >= totalDias;

    const asistenciaCompleta =
      totalDias > 0 &&
      Number(info?.diasConAsistencia || 0) >= totalDias;

    if (fotograficoCompleto && asistenciaCompleta) {
      return {
        key: "completa",
        texto: "Soportes completos",
        color: "#198754",
        fondo: "#f0fff4",
        borde: "#badbcc",
      };
    }

    const tieneAlgunSoporte =
      Number(info?.diasConFotografico || 0) > 0 ||
      Number(info?.diasConAsistencia || 0) > 0;

    if (tieneAlgunSoporte) {
      return {
        key: "parcial",
        texto: "Soportes parciales",
        color: "#b26a00",
        fondo: "#fffaf0",
        borde: "#ffe69c",
      };
    }

    return {
      key: "pendiente",
      texto: "Faltan soportes",
      color: "#dc3545",
      fondo: "#fff5f5",
      borde: "#f1aeb5",
    };
  }, []);

  // ==============================
  // ABRIR INFORMACIÓN AL PASAR
  // SOBRE LA ORDEN DE VIAJE
  // ==============================
  const calcularPosicionDetalleOrden = useCallback((elemento) => {
    const rect = elemento.getBoundingClientRect();
    const anchoTarjeta = 320;
    const altoEstimado = 285;
    const margen = 12;

    let left = rect.left;
    let top = rect.bottom + 8;

    if (left + anchoTarjeta > window.innerWidth - margen) {
      left = window.innerWidth - anchoTarjeta - margen;
    }

    if (left < margen) {
      left = margen;
    }

    if (top + altoEstimado > window.innerHeight - margen) {
      top = Math.max(
        margen,
        rect.top - altoEstimado - 8
      );
    }

    setPosicionDetalleOrden({
      top,
      left,
    });
  }, []);

  const mostrarDetalleOrden = useCallback(
    (event, agenda, info) => {
      if (!info || detalleOrdenFijado) return;

      calcularPosicionDetalleOrden(event.currentTarget);

      setDetalleOrden({
        agenda,
        info,
      });
    },
    [calcularPosicionDetalleOrden, detalleOrdenFijado]
  );

  const seleccionarDetalleOrden = useCallback(
    (event, agenda, info) => {
      if (!info) return;

      event.stopPropagation();

      const mismaOrden =
        detalleOrden?.agenda?._id === agenda?._id &&
        detalleOrdenFijado;

      if (mismaOrden) {
        setDetalleOrden(null);
        setDetalleOrdenFijado(false);
        return;
      }

      calcularPosicionDetalleOrden(event.currentTarget);

      setDetalleOrden({
        agenda,
        info,
      });

      setDetalleOrdenFijado(true);
    },
    [
      calcularPosicionDetalleOrden,
      detalleOrden,
      detalleOrdenFijado,
    ]
  );

  const ocultarDetalleOrden = useCallback(() => {
    if (!detalleOrdenFijado) {
      setDetalleOrden(null);
    }
  }, [detalleOrdenFijado]);

  const cerrarDetalleOrden = useCallback(() => {
    setDetalleOrden(null);
    setDetalleOrdenFijado(false);
  }, []);

  // ==============================
  // DATA TABLE
  // ==============================
  const dataTabla = useMemo(
    () =>
      agendas.map((agenda, index) => ({
        ...agenda,

        // La agenda más reciente aparece primero,
        // pero conserva el número de ítem más alto.
        item: agendas.length - index,

        numeroOrdenViaje: (() => {
          const info = ordenesViaje[agenda._id] || null;
          const estadoVisual = obtenerEstadoVisualOrden(info);

          const estaSeleccionada =
            detalleOrdenFijado &&
            detalleOrden?.agenda?._id === agenda._id;

          return (
            <button
              type="button"
              className="btn border-0 text-start w-100"
              onMouseEnter={(event) =>
                mostrarDetalleOrden(event, agenda, info)
              }
              onMouseLeave={ocultarDetalleOrden}
              onFocus={(event) =>
                mostrarDetalleOrden(event, agenda, info)
              }
              onBlur={ocultarDetalleOrden}
              onClick={(event) =>
                seleccionarDetalleOrden(event, agenda, info)
              }
              title={
                estaSeleccionada
                  ? "Clic para cerrar el detalle"
                  : "Ubíquese o haga clic para ver el detalle"
              }
              style={{
                boxShadow: "none",
                minWidth: 105,
                padding: "7px 10px",
                borderRadius: 8,
                color: estadoVisual.color,
                backgroundColor: estaSeleccionada
                  ? estadoVisual.fondo
                  : "transparent",
                cursor: "pointer",
                transition:
                  "background-color 0.15s ease, color 0.15s ease",
              }}
            >
              <div className="d-flex align-items-center gap-2">
                <span
                  aria-hidden="true"
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: estadoVisual.color,
                    flex: "0 0 auto",
                  }}
                ></span>

                <span
                  className="fw-bold"
                  style={{
                    color: estadoVisual.color,
                    fontSize: "0.92rem",
                    whiteSpace: "nowrap",
                  }}
                >
                  {info?.numeroOrdenViaje || "Sin orden"}
                </span>
              </div>
            </button>
          );
        })(),

        ruta: (
          <div style={{ lineHeight: "1.4" }}>
            {String(agenda.ruta || "")
              .split(".")
              .filter(Boolean)
              .map((item, indexRuta) => (
                <div key={indexRuta} className="small text-dark">
                  • {item.trim()}
                </div>
              ))}
          </div>
        ),

        entidad: (
          <div style={{ lineHeight: "1.4" }}>
            {String(agenda.entidad || "")
              .split(".")
              .filter(Boolean)
              .map((item, indexEntidad) => (
                <div key={indexEntidad} className="small text-dark">
                  • {item.trim()}
                </div>
              ))}
          </div>
        ),

        contactoEntidad: (
          <div style={{ lineHeight: "1.4" }}>
            {String(agenda.contactoEntidad || "")
              .split(".")
              .filter(Boolean)
              .map((item, indexContacto) => (
                <div key={indexContacto} className="small text-dark">
                  • {item.trim()}
                </div>
              ))}
          </div>
        ),

        fechaInicioDesplazamiento: formatearFechaSinDesfase(
          agenda.fechaInicioDesplazamiento
        ),

        fechaFinDesplazamiento: formatearFechaSinDesfase(
          agenda.fechaFinDesplazamiento
        ),

        totalDias: Array.isArray(agenda.agendaDias)
          ? agenda.agendaDias.length
          : 0,

        totalObligaciones: Array.isArray(agenda.obligacionesContractuales)
          ? agenda.obligacionesContractuales.length
          : 0,

        totalGastos: new Intl.NumberFormat("es-CO", {
          style: "currency",
          currency: "COP",
          maximumFractionDigits: 0,
        }).format(
          Number(agenda.gastosaereo || 0) +
          Number(agenda.gastosterrestre || 0) +
          Number(agenda.gastosintermunicipal || 0)
        ),

        funciones: (
          <>
            {/* ESCRITORIO - ORIGINAL */}
            <div className="d-none d-md-flex gap-2 flex-wrap justify-content-end">
              <button
                className="btn btn-outline-success btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() => descargarAgendaExcel(agenda)}
                title="Descargar agenda en Excel"
                type="button"
                disabled={
                  loading ||
                  deletingId === agenda._id ||
                  duplicatingId === agenda._id ||
                  downloadingId === agenda._id
                }
              >
                {downloadingId === agenda._id ? (
                  <i className="fa-solid fa-spinner fa-spin"></i>
                ) : (
                  <i className="fa-solid fa-download"></i>
                )}
              </button>

              <button
                className="btn btn-outline-dark btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() =>
                  navigate(`/instructor/legalizacionviaticos/listar/${agenda._id}`)
                }
                title="Ver registros de legalización"
                type="button"
                disabled={
                  loading ||
                  deletingId === agenda._id ||
                  duplicatingId === agenda._id ||
                  downloadingId === agenda._id
                }
              >
                <i className="fa-solid fa-file-invoice"></i>
              </button>

              {puedeActualizar && (
                <button
                  className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
                  style={{ width: 34, height: 34 }}
                  onClick={() =>
                    navigate(`/instructor/agendas/editar/${agenda._id}`)
                  }
                  title="Editar agenda"
                  type="button"
                  disabled={
                    loading ||
                    deletingId === agenda._id ||
                    duplicatingId === agenda._id ||
                    downloadingId === agenda._id
                  }
                >
                  <i className="fa-solid fa-pen-to-square"></i>
                </button>
              )}

              {puedeCrear && (
                <button
                  className="btn btn-outline-primary btn-sm d-inline-flex align-items-center justify-content-center"
                  style={{ width: 34, height: 34 }}
                  onClick={() => duplicarAgenda(agenda._id)}
                  title="Duplicar agenda"
                  type="button"
                  disabled={
                    loading ||
                    deletingId === agenda._id ||
                    duplicatingId === agenda._id ||
                    downloadingId === agenda._id
                  }
                >
                  {duplicatingId === agenda._id ? (
                    <i className="fa-solid fa-spinner fa-spin"></i>
                  ) : (
                    <i className="fa-solid fa-copy"></i>
                  )}
                </button>
              )}

              {puedeEliminar && (
                <button
                  className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                  style={{ width: 34, height: 34 }}
                  onClick={() => deleteAgenda(agenda._id)}
                  title="Eliminar agenda"
                  type="button"
                  disabled={
                    loading ||
                    deletingId === agenda._id ||
                    duplicatingId === agenda._id ||
                    downloadingId === agenda._id
                  }
                >
                  {deletingId === agenda._id ? (
                    <i className="fa-solid fa-spinner fa-spin text-white"></i>
                  ) : (
                    <i className="fa-solid fa-trash"></i>
                  )}
                </button>
              )}
            </div>

            {/* RESPONSIVE - 2 BOTONES POR LÍNEA */}
            <div className="d-flex d-md-none gap-2 flex-wrap justify-content-end">
              <button
                className="btn btn-outline-success btn-sm d-flex flex-column align-items-center justify-content-center"
                style={{ width: "calc(50% - 0.25rem)" }}
                type="button"
                title="Descargar agenda"
                onClick={() => descargarAgendaExcel(agenda)}
                disabled={
                  loading ||
                  deletingId === agenda._id ||
                  duplicatingId === agenda._id ||
                  downloadingId === agenda._id
                }
              >
                {downloadingId === agenda._id ? (
                  <i className="fa-solid fa-spinner fa-spin mb-1"></i>
                ) : (
                  <i className="fa-solid fa-file-excel mb-1"></i>
                )}
                <small>Excel</small>
              </button>

              <button
                className="btn btn-outline-dark btn-sm d-flex flex-column align-items-center justify-content-center"
                style={{ width: "calc(50% - 0.25rem)" }}
                type="button"
                title="Ver registros de legalización"
                onClick={() =>
                  navigate(`/instructor/legalizacionviaticos/listar/${agenda._id}`)
                }
                disabled={
                  loading ||
                  deletingId === agenda._id ||
                  duplicatingId === agenda._id ||
                  downloadingId === agenda._id
                }
              >
                <i className="fa-solid fa-file-invoice mb-1"></i>
                <small>Legalización</small>
              </button>

              {puedeCrear && (
                <button
                  className="btn btn-outline-primary btn-sm d-flex flex-column align-items-center justify-content-center"
                  style={{ width: "calc(50% - 0.25rem)" }}
                  type="button"
                  title="Duplicar agenda"
                  onClick={() => duplicarAgenda(agenda._id)}
                  disabled={
                    loading ||
                    deletingId === agenda._id ||
                    duplicatingId === agenda._id ||
                    downloadingId === agenda._id
                  }
                >
                  {duplicatingId === agenda._id ? (
                    <i className="fa-solid fa-spinner fa-spin mb-1"></i>
                  ) : (
                    <i className="fa-solid fa-copy mb-1"></i>
                  )}
                  <small>Duplicar</small>
                </button>
              )}

              {puedeActualizar && (
                <button
                  className="btn btn-outline-warning btn-sm d-flex flex-column align-items-center justify-content-center"
                  style={{ width: "calc(50% - 0.25rem)" }}
                  type="button"
                  title="Editar agenda"
                  onClick={() =>
                    navigate(`/instructor/agendas/editar/${agenda._id}`)
                  }
                  disabled={
                    loading ||
                    deletingId === agenda._id ||
                    duplicatingId === agenda._id ||
                    downloadingId === agenda._id
                  }
                >
                  <i className="fa-solid fa-pen-to-square mb-1"></i>
                  <small>Editar</small>
                </button>
              )}

              {puedeEliminar && (
                <button
                  className="btn btn-outline-danger btn-sm d-flex flex-column align-items-center justify-content-center"
                  style={{ width: "calc(50% - 0.25rem)" }}
                  type="button"
                  title="Eliminar agenda"
                  onClick={() => deleteAgenda(agenda._id)}
                  disabled={
                    loading ||
                    deletingId === agenda._id ||
                    duplicatingId === agenda._id ||
                    downloadingId === agenda._id
                  }
                >
                  {deletingId === agenda._id ? (
                    <i className="fa-solid fa-spinner fa-spin mb-1"></i>
                  ) : (
                    <i className="fa-solid fa-trash mb-1"></i>
                  )}
                  <small>Eliminar</small>
                </button>
              )}
            </div>
          </>
        ),
      })),
    [
      agendas,
      ordenesViaje,
      obtenerEstadoVisualOrden,
      mostrarDetalleOrden,
      seleccionarDetalleOrden,
      ocultarDetalleOrden,
      detalleOrden,
      detalleOrdenFijado,
      loading,
      deletingId,
      duplicatingId,
      downloadingId,
      puedeActualizar,
      puedeCrear,
      puedeEliminar,
      navigate,
      deleteAgenda,
      duplicarAgenda,
      descargarAgendaExcel,
      formatearFechaSinDesfase,
    ]
  );

  const columns = useMemo(
    () => [
      { Header: "#", accessor: "item" },
      { Header: "Orden", accessor: "numeroOrdenViaje" },
      { Header: "Ruta", accessor: "ruta" },
      { Header: "Entidad", accessor: "entidad" },
      //{ Header: "Contacto", accessor: "contactoEntidad" },
      { Header: "Inicio", accessor: "fechaInicioDesplazamiento" },
      { Header: "Fin", accessor: "fechaFinDesplazamiento" },
      { Header: "Días", accessor: "totalDias" },
      { Header: "Total Gastos", accessor: "totalGastos" },
      { Header: "Acciones", accessor: "funciones" },
    ],
    []
  );

  // ==============================
  // RENDER
  // ==============================
  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  Agendas de Viáticos
                </h5>

                <small className="text-secondary">
                  Gestión de agendas de viaje asociadas al contrato.
                </small>
              </div>

              <div className="d-flex gap-2 flex-nowrap">
                <button
                  className="btn btn-outline-dark"
                  type="button"
                  onClick={() => navigate(-1)}
                  title="Volver"
                  disabled={loading}
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>

                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={getData}
                  disabled={loading}
                  title="Actualizar"
                >
                  <i
                    className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-rotate"
                      } me-2`}
                  ></i>
                  Actualizar
                </button>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    disabled={loading}
                    onClick={() => navigate(`/instructor/agendas/nuevo/${id}`)}
                    title="Nueva Agenda de Viáticos"
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Nuevo
                  </button>
                )}
              </div>
            </div>
          </div>

          <div>
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div
                  className="spinner-border"
                  role="status"
                  aria-hidden="true"
                ></div>

                <div className="mt-3">Cargando agendas…</div>
              </div>
            ) : dataTabla.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable columns={columns} data={dataTabla} />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">
                <div className="display-6 mb-2">✈️</div>

                <div className="fw-semibold">
                  No hay agendas de viáticos registradas
                </div>

                <div className="small mb-3">
                  Este contrato aún no tiene agendas asociadas.
                </div>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    onClick={() => navigate(`/instructor/agendas/nuevo/${id}`)}
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Crear agenda
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ==============================
          DETALLE FLOTANTE ORDEN DE VIAJE
          ============================== */}
      {detalleOrden && (() => {
        const info = detalleOrden.info || {};
        const estadoVisual = obtenerEstadoVisualOrden(info);

        return (
          <div
            role="status"
            aria-live="polite"
            style={{
              position: "fixed",
              top: posicionDetalleOrden.top,
              left: posicionDetalleOrden.left,
              width: 320,
              maxWidth: "calc(100vw - 24px)",
              zIndex: 9999,
              background: "#ffffff",
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              boxShadow: "0 12px 35px rgba(0,0,0,0.15)",
              overflow: "hidden",
              pointerEvents: detalleOrdenFijado ? "auto" : "none",
            }}
            onMouseEnter={() => {
              if (detalleOrdenFijado) {
                setDetalleOrdenFijado(true);
              }
            }}
          >
            <div
              className="px-3 py-2 d-flex justify-content-between align-items-center"
              style={{
                borderBottom: "1px solid #eeeeee",
              }}
            >
              <div>
                <div
                  className="text-secondary"
                  style={{
                    fontSize: "0.68rem",
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                  }}
                >
                  Orden de viaje
                </div>

                <div
                  className="fw-bold"
                  style={{
                    color: estadoVisual.color,
                    fontSize: "1.08rem",
                  }}
                >
                  {info?.numeroOrdenViaje || "Sin Orden"}
                </div>
              </div>

              <div className="d-flex align-items-center gap-2">
                <span
                  className="badge"
                  style={{
                    color: estadoVisual.color,
                    backgroundColor: estadoVisual.fondo,
                    border: `1px solid ${estadoVisual.borde}`,
                  }}
                >
                  {estadoVisual.texto}
                </span>

                {detalleOrdenFijado && (
                  <button
                    type="button"
                    className="btn btn-sm btn-light border d-inline-flex align-items-center justify-content-center"
                    onClick={cerrarDetalleOrden}
                    title="Cerrar detalle"
                    style={{
                      width: 28,
                      height: 28,
                      padding: 0,
                      borderRadius: 7,
                    }}
                  >
                    <i className="fa-solid fa-xmark"></i>
                  </button>
                )}
              </div>
            </div>

            <div className="p-3">
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <div
                    className="border rounded-3 p-2 h-100"
                    style={{ background: "#fafafa" }}
                  >
                    <div className="small text-secondary mb-1">
                      <i className="fa-solid fa-camera me-1"></i>
                      Evidencias
                    </div>

                    <div className="fw-semibold">
                      {info?.diasConFotografico || 0}/
                      {info?.totalDias || 0} días
                    </div>

                    <div className="small text-secondary">
                      {info?.totalFotografias || 0} archivo(s)
                    </div>
                  </div>
                </div>

                <div className="col-6">
                  <div
                    className="border rounded-3 p-2 h-100"
                    style={{ background: "#fafafa" }}
                  >
                    <div className="small text-secondary mb-1">
                      <i className="fa-solid fa-clipboard-user me-1"></i>
                      Asistencia
                    </div>

                    <div className="fw-semibold">
                      {info?.diasConAsistencia || 0}/
                      {info?.totalDias || 0} días
                    </div>

                    <div className="small text-secondary">
                      {info?.totalAsistencias || 0} archivo(s)
                    </div>
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-between py-1">
                <span className="small text-secondary">
                  Días de agenda
                </span>

                <span className="small fw-semibold">
                  {info?.totalDias || 0}
                </span>
              </div>

              {info?.fechaPresentacion && (
                <div className="d-flex justify-content-between py-1">
                  <span className="small text-secondary">
                    Fecha presentación
                  </span>

                  <span className="small fw-semibold">
                    {formatearFechaSinDesfase(
                      info.fechaPresentacion
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default ListarAgendasViatico;